import DOMPurify from 'dompurify';
import React from 'react';

/**
 * Normalise un nom ou prénom de personne :
 * - Convertit en majuscules
 * - Supprime les accents (É → E, Ç → C, etc.)
 * - Supprime les caractères spéciaux (garde lettres, espaces, tirets, apostrophes)
 * - Supprime les espaces superflus
 */
export const normalizePersonName = (name: string): string => {
    if (!name) return name;
    return name
        .toUpperCase()
        .trim()
        // Supprime les accents (décompose puis retire les diacritiques)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        // Garde uniquement : lettres A-Z, espaces, tirets, apostrophes
        .replace(/[^A-Z\s'-]/g, '')
        // Remplace les espaces multiples par un seul
        .replace(/\s+/g, ' ')
        .trim();
};

export const normalizeEuro = (number: number): string => {
    return new Intl.NumberFormat('fr-FR', { style : 'currency', currency: 'EUR', }).format(number).replace(/\u00A0/g, '');
};

/**
 * Formate une devise en euros, gère null/undefined
 * Retourne 'N/A' si la valeur est absente
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    return `${numValue.toFixed(2)} €`;
};

/**
 * Parse une date string en objet Date local.
 * Les dates "date-only" (YYYY-MM-DD) sont parsées en local pour éviter le décalage UTC.
 */
const parseDateString = (date: string): Date => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        const [year, month, day] = date.split('-').map(Number);
        const d = new Date(year, month - 1, day);
        // Vérifier que la date n'a pas "rollé" (ex: mois 13 → mois 1 de l'année suivante)
        if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) {
            return new Date(NaN);
        }
        return d;
    }
    return new Date(date);
};

/**
 * Formate une date au format français (dd/MM/yyyy)
 * Retourne 'N/A' si la date est absente
 */
export const formatDate = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? parseDateString(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return new Intl.DateTimeFormat('fr-FR').format(dateObj);
};

/**
 * Formate une date avec l'heure au format français (dd/MM/yyyy HH:mm)
 * Retourne 'N/A' si la date est absente
 */
export const formatDateTime = (date: string | Date | null | undefined): string => {
    if (!date) return 'N/A';
    const dateObj = typeof date === 'string' ? parseDateString(date) : date;
    if (isNaN(dateObj.getTime())) return 'N/A';
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(dateObj);
};

/**
 * Formate un pourcentage
 * 0.13 → 13%, 0.1300 → 13%
 */
export const formatPercent = (
    value: number | string | null | undefined,
    decimals: number = 0
): string => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return 'N/A';
    return `${(numValue * 100).toFixed(decimals)}%`;
};

/**
 * Récupère le label depuis un tableau de constantes { value, label }
 * Exemple: getLabelFromValue(civilites, '1') → 'Monsieur'
 * Supporte des valeurs de type string ou number
 */
export const getLabelFromValue = <T extends { value: string | number; label: string }>(
    array: readonly T[],
    value: string | number | null | undefined
): string => {
    if (value === null || value === undefined) return 'N/A';
    const item = array.find((item) => String(item.value) === String(value));
    return item?.label || String(value);
};

/**
 * Ajoute des espaces insécables avant les caractères de ponctuation double (? ! : ;)
 * selon les règles typographiques françaises
 */
export const addNonBreakingSpaces = (text: string): string => {
  if (!text) return text;
  
  // Remplace les espaces avant les ponctuations doubles par des espaces insécables
  // et ajoute un espace insécable si absent
  return text
    .replace(/\s*([?!:;])/g, '\u00A0$1')
    // Gère aussi les guillemets français
    .replace(/«\s*/g, '«\u00A0')
    .replace(/\s*»/g, '\u00A0»');
};

/**
 * Traite les children React pour appliquer les espaces insécables
 * Parcourt récursivement les éléments React
 */
export const formatWithNonBreakingSpaces = (children: React.ReactNode): React.ReactNode => {
  const processNode = (node: React.ReactNode, key?: string | number): React.ReactNode => {
    // Traite les strings
    if (typeof node === 'string') {
      const formatted = addNonBreakingSpaces(node);
      // Si le texte contient des balises HTML, on le rend via dangerouslySetInnerHTML avec sanitization
      if (/<\w+[^>]*>/.test(formatted)) {
        return React.createElement('span', { key, dangerouslySetInnerHTML: { __html: DOMPurify.sanitize(formatted) } });
      }
      // Si le texte contient des espaces insécables, on le wrappe dans un span
      if (formatted !== node && formatted.includes('\u00A0')) {
        return React.createElement('span', { key }, formatted);
      }
      return formatted;
    }
    
    // Traite les nombres
    if (typeof node === 'number') {
      return processNode(node.toString(), key);
    }
    
    // Traite les tableaux
    if (Array.isArray(node)) {
      return node.map((child, index) => {
        const processed = processNode(child, index);
        // why: les enfants JSX positionnels n'ont pas de clé une fois transformés en tableau
        return React.isValidElement(processed) && processed.key === null ? React.cloneElement(processed, { key: index }) : processed;
      });
    }
    
    // Traite les éléments React
    if (React.isValidElement(node)) {
      // Type assertion pour accéder aux props
      const element = node as React.ReactElement<{ children?: React.ReactNode }>;
      // Clone l'élément avec les children traités
      const childrenProcessed = processNode(element.props.children);
      if (childrenProcessed !== element.props.children) {
        return React.cloneElement(element, {}, childrenProcessed);
      }
      return element;
    }
    
    return node;
  };
  
  return processNode(children);
};

/**
 * Formate un numéro de téléphone par paires de chiffres séparées par un espace (10 chiffres max) :
 * "0612345678" → "06 12 34 56 78". Valeur vide renvoyée telle quelle.
 */
export const normalizeTel = <T extends string | null | undefined>(value: T): T | string => {
  if (!value) return value;

  const digits = value.replace(/\D/g, '').slice(0, 10);
  return (digits.match(/.{1,2}/g) ?? []).join(' ');
};
